import {
  Button,
  SimpleTooltip,
  Stack,
  Input,
  InputWrapper
} from 'design-system'
import { Split, Search, ClipboardCopy, ClipboardPaste } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import Modal from 'src/components/generics/headless/modal'
import ToggleSwitch from 'src/components/generics/inputs/ToggleSwitch'
import { InfoTooltip } from 'src/components/points/info-tooltip'
import { MainNavLink } from 'src/containers/Navigation/main-nav/main-nav-link'
import { useRouter } from 'src/lib/router'

import { useFlags } from './feature-flags'
import { useFliptContext } from './flipt/flipt-provider'
import {
  FF_DEFAULT_VALUE,
  useLocalStorageFF
} from './local-storage-feature-flags'
import { useBannerActions } from 'src/components/banner-callout/use-banner-actions'

export const FeatureFlagsNavItem = () => {
  const { setBanner } = useBannerActions()
  const router = useRouter()
  const { showFlagsUi } = router.query
  const [isModalOpen, setIsModalOpen] = useState(false)

  const flags = useFlags()
  const fliptContext = useFliptContext()
  const { flags: fliptFlags } = fliptContext || { flags: {} }
  const { ff, setFF } = useLocalStorageFF()

  const flagsOptions: [string, boolean][] = Object.entries(flags)
    .map(([key, value]) => {
      return [key, Boolean(value)] as [string, boolean]
    })
    .sort(([a], [b]) => a.localeCompare(b))

  const [searchQuery, setSearchQuery] = useState('')

  const filteredFlagsOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return flagsOptions
    return flagsOptions.filter(([key]) => {
      const nameMatches = key.toLowerCase().includes(query)
      const description = fliptFlags[key]?.description || ''
      const descriptionMatches = description.toLowerCase().includes(query)
      return nameMatches || descriptionMatches
    })
  }, [flagsOptions, fliptFlags, searchQuery])

  const [shouldShowFlagsUi, setShouldShowFlagsUi] = useState(false)

  useEffect(() => {
    if (showFlagsUi === 'true' && !ff?.uiEnabled) {
      setFF({ ...ff, uiEnabled: true })
      setShouldShowFlagsUi(true)
      return
    }

    if (showFlagsUi === 'false' && ff?.uiEnabled) {
      setFF({ ...ff, uiEnabled: false })
      setShouldShowFlagsUi(false)
      return
    }

    if (ff?.uiEnabled) {
      setShouldShowFlagsUi(true)
    }
  }, [showFlagsUi, ff?.uiEnabled])

  if (!shouldShowFlagsUi) return null

  const onModalClose = () => setIsModalOpen(false)
  const onModalOpen = () => setIsModalOpen(true)

  const onFlagChange = (flag: string) => () => {
    const { uiEnabled } = ff

    const newFlags = { ...flags, [flag]: !flags[flag] }
    setFF({ uiEnabled, flags: newFlags })
  }

  const onResetFlags = () => {
    setFF({ uiEnabled: ff?.uiEnabled, flags: {} })
  }

  const onDisableFlags = () => {
    const newFlags = Object.keys(flags).reduce(
      (acc, flag) => {
        acc[flag] = false
        return acc
      },
      {} as Record<string, boolean>
    )
    setFF({ uiEnabled: ff?.uiEnabled, flags: newFlags })
  }

  const onHideFlagsUi = () => {
    onModalClose()
    setFF(FF_DEFAULT_VALUE)
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, showFlagsUi: false }
      },
      undefined,
      { shallow: true }
    )
  }

  const onCopyFlags = () => {
    navigator.clipboard.writeText(JSON.stringify(ff.flags))
    setBanner({
      open: true,
      message: 'Flags copied to clipboard!',
      calloutType: 'success'
    })
  }

  const onPasteFlags = async () => {
    try {
      const pastedFlags = await navigator.clipboard.readText()
      const parsedFlags = JSON.parse(pastedFlags) as Record<string, boolean>
      setFF({ uiEnabled: ff?.uiEnabled, flags: parsedFlags })
      setBanner({
        open: true,
        message: 'Flags pasted from clipboard!',
        calloutType: 'success'
      })
    } catch {
      setBanner({
        open: true,
        message: 'Failed to paste flags from clipboard!',
        calloutType: 'error'
      })
    }
  }

  return (
    <li>
      <MainNavLink
        onClick={onModalOpen}
        label="Feature Flags"
        className="rounded-full bg-purple-100 text-purple-700 before:bg-purple-700"
        icon={<Split aria-hidden="true" size={24} />}
      />
      {/* @ts-expect-error Modal doesn't recognize props correctly because of .js */}
      <Modal
        large
        open={isModalOpen}
        title="Local Feature Flags"
        onRequestClose={onModalClose}>
        <Stack gap={2} className="p-4">
          <InputWrapper leftIconSlot={<Search />}>
            <Input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search flags by name or description"
              aria-label="Search feature flags"
            />
          </InputWrapper>

          <Stack
            as="ul"
            role="list"
            gap={2}
            className="mb-4 max-h-[500px] overflow-y-auto">
            {filteredFlagsOptions.length === 0 ? (
              <li className="px-1 text-sm text-gray-500">No flags found</li>
            ) : null}
            {filteredFlagsOptions.map(([key, value]) => (
              <li key={`flag-${key}`} className="flex justify-between">
                <p className="flex grow items-center gap-x-2">
                  {key}
                  {fliptFlags[key]?.description ? (
                    <InfoTooltip label={`About ${key}`}>
                      {fliptFlags[key].description}
                    </InfoTooltip>
                  ) : null}
                </p>
                <ToggleSwitch
                  id="toggle-posts"
                  checked={value}
                  onChange={onFlagChange(key)}
                  className="shrink-0"
                />
              </li>
            ))}
          </Stack>
          <footer className="flex justify-between">
            <div className="flex items-center gap-x-2">
              <Button buttonType="gray" onClick={onDisableFlags}>
                Disable all flags
              </Button>
              <Button buttonType="gray" onClick={onResetFlags}>
                Reset all local flags
              </Button>
              <Button buttonType="gray" onClick={onCopyFlags}>
                <ClipboardCopy size={16} aria-hidden />
              </Button>
              <Button buttonType="gray" onClick={onPasteFlags}>
                <ClipboardPaste size={16} aria-hidden />
              </Button>
            </div>
            <SimpleTooltip content="Note: it will hide UI button and reset all local flags">
              <Button buttonType="red" onClick={onHideFlagsUi}>
                Hide flags UI
              </Button>
            </SimpleTooltip>
          </footer>
        </Stack>
      </Modal>
    </li>
  )
}
